import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";
import { selectRoomsRestSet, selectSelectedRoom } from "../../../store/room/room-selectors";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { EnvironmentIcon, RobotVisibilityIcon } from "../../../utils/icons/icons";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import EnvironmentCleaningConfiguration from "../../Configuration/CleaningConfiguration/global/EnvironmentCleaningConfiguration";
import VisibilityConfiguration from "../../Configuration/CleaningConfiguration/global/VisibilityConfiguration";
import {
  CleaningConfiguration,
  CleaningConfigurationLevel,
} from "../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import {
  selectGlobalCleaningConfiguration,
  selectGlobalMapRulesConfiguration,
  selectParamListRestState,
} from "../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import {
  isMapRulesConfiguration,
  MapRulesConfiguration,
} from "../../../interfaces/robot/cleaning-configurations/map-rules-configuration";
import ReadOnlyProtectedForm from "../../Inputs/ReadOnlyProtectedForm";

const RoomCleaningConfiguration: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getParamList, patchRoomById, patchRoomsRestSet } = useSharedActions();

  const room = useSharedSelector((state) => selectSelectedRoom(state));
  const globalCleaningConfiguration = useSharedSelector((state) => selectGlobalCleaningConfiguration(state));
  const globalMapRulesConfiguration = useSharedSelector((state) => selectGlobalMapRulesConfiguration(state));
  const paramListRest = useSharedSelector((state) => selectParamListRestState(state));
  const roomRestSet = useSharedSelector((state) => selectRoomsRestSet(state));

  const [saveLock, setSaveLock] = useState(false);

  useEffect(() => {
    if (paramListRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getParamList());
    }
  }, [dispatch, getParamList, paramListRest.getStatus]);

  const handleUpdateRoomConfiguration = (patch: Partial<CleaningConfiguration> | Partial<MapRulesConfiguration>) => {
    if (room == null) return;
    setSaveLock(true);
    dispatch(
      patchRoomById({
        id: room.id,
        data: isMapRulesConfiguration(patch) ? { mapRulesConfiguration: patch } : { cleaningConfiguration: patch },
      }),
    );
  };

  const snackbarCloseHandler = () => {
    setSaveLock(false);
    dispatch(patchRoomsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("configuration-cleaning")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <EnvironmentIcon />
                  <Typography>{t("label-robot-environment-configuration")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack>
                <EnvironmentCleaningConfiguration
                  configurationLevel={CleaningConfigurationLevel.Room}
                  globalCleaningConfiguration={globalCleaningConfiguration}
                  roomCleaningConfiguration={room?.cleaningConfiguration}
                  globalMapRulesConfiguration={globalMapRulesConfiguration}
                  roomMapRulesConfiguration={room?.mapRulesConfiguration}
                  roomHasMapFiles={room?.hasPosegraphAndDataFiles}
                  onConfigurationChange={handleUpdateRoomConfiguration}
                />
              </Stack>
            ),
          },
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <RobotVisibilityIcon />
                  <Typography>{t("label-robot-visibility-configuration")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <ReadOnlyProtectedForm>
                <Stack>
                  <VisibilityConfiguration
                    configurationLevel={CleaningConfigurationLevel.Room}
                    globalMapRulesConfiguration={globalMapRulesConfiguration}
                    roomMapRulesConfiguration={room?.mapRulesConfiguration}
                    onMapRulesConfigurationChange={handleUpdateRoomConfiguration}
                  />
                </Stack>
              </ReadOnlyProtectedForm>
            ),
          },
        ]}
      />
      <CustomSnackbar
        title={t("error-saving-cleaning-configuration")}
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Failed}
        onClose={snackbarCloseHandler}
        textMessages={getSaveErrorMessages(roomRestSet.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-cleaning-configuration")}
        severity="success"
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Success}
        onClose={snackbarCloseHandler}
      />
    </React.Fragment>
  );
};

export default RoomCleaningConfiguration;
